import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ENVIRONMENT from '../../../../environments';
import CustomerMainLayout from '../CustomerMainLayout';
import TwoDImagesModal from './TwoDImagesModal';
import AIEditorModalViewer from '../AIEditorModalViewer/AIEditorModalViewer';

const getIframeUserContextPayload = () => {
  const keys = [
    'username',
    'company_id',
    'shared_entities',
    'all3d_jwt_token',
    'user_role',
    'is_admin',
    'is_msprovider',
    'managed_company_id',
    'managed_customer_username',
    'managed_shared_entites',
    'super_admin_username',
    'is_ai_customer',
  ];

  return keys.reduce((acc, key) => {
    const value = localStorage.getItem(key);
    if (value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const createAiModalState = (overrides = {}) => ({
  isOpen: false,
  imageData: null,
  currentIndex: 0,
  navigationImageIds: [],
  isNavigating: false,
  pendingRequestId: null,
  sessionId: 0,
  ...overrides,
});

const normalizeNavigationImageIds = (navigationImageIds, selectedImageData) => {
  if (Array.isArray(navigationImageIds) && navigationImageIds.length > 0) {
    return navigationImageIds;
  }

  const selectedImageId = selectedImageData?.render_id || selectedImageData?.id;
  return selectedImageId !== undefined && selectedImageId !== null ? [selectedImageId] : [];
};

const MigratedImageGallery = ({ location }) => {
  const iframeRef = useRef(null);
  const [standardModalState, setStandardModalState] = React.useState({
    isOpen: false,
    images: [],
    currentIndex: 0,
    currentType: 'all',
    imagesCount: {},
  });
  const [aiModalState, setAiModalState] = React.useState(() => createAiModalState());
  const aiModalStateRef = useRef(aiModalState);
  const aiModalSessionRef = useRef(0);
  const aiModalRequestRef = useRef(0);
  const [compareSiloModalOpen, setCompareSiloModalOpen] = React.useState(false);
  const migratedPortalBaseUrl = (
    ENVIRONMENT.MIGRATED_PORTAL_URL ||
    process.env.REACT_APP_MIGRATED_PORTAL_URL ||
    'http://localhost:5173'
  ).replace(/\/$/, '');
  const iframeSrc = `${migratedPortalBaseUrl}/image-gallery${location?.search || ''}`;

  const targetOrigin = useMemo(() => {
    try {
      return new URL(migratedPortalBaseUrl).origin;
    } catch (error) {
      return '*';
    }
  }, [migratedPortalBaseUrl]);

  const postUserContext = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;

    iframeWindow.postMessage(
      {
        type: 'ALL3D_USER_CONTEXT',
        payload: getIframeUserContextPayload(),
      },
      targetOrigin
    );
  }, [targetOrigin]);

  const syncAiModalState = useCallback((nextStateOrUpdater) => {
    const nextState = typeof nextStateOrUpdater === 'function'
      ? nextStateOrUpdater(aiModalStateRef.current)
      : nextStateOrUpdater;

    aiModalStateRef.current = nextState;
    setAiModalState(nextState);
  }, []);

  const buildAiModalImageData = useCallback((image, previousImageData = null) => {
    if (!image) return null;

    const selectedImageUrl = (
      image.selectedImageUrl ||
      image.originalSrc ||
      image.src ||
      image.uri ||
      ''
    ).replace(/low_res_qa_img_uri/i, 'qa_img_uri').replace('_512.', '.');

    return {
      ...image,
      ...(previousImageData || {}),
      selectedImageUrl,
      renderIndex: 0,
      renders: Array.isArray(image.renders) && image.renders.length > 0 ? image.renders : [selectedImageUrl],
      asset_type: image.image_type === 'ai_video' ? 'ai_video' : (image.asset_type || 'ai_image'),
      prompt_text: image.prompt_text || image.display_name || '',
    };
  }, []);

  const closeStandardModal = useCallback(() => {
    setStandardModalState((prev) => ({
      ...prev,
      isOpen: false,
      images: [],
      currentIndex: 0,
    }));
  }, []);

  const closeAiModal = useCallback(() => {
    aiModalSessionRef.current += 1;
    syncAiModalState(createAiModalState({
      sessionId: aiModalSessionRef.current,
    }));
    setCompareSiloModalOpen(false);
  }, [syncAiModalState]);

  const updateAiModalImageName = useCallback((imageData, updatedDisplayName) => {
    syncAiModalState((prev) => {
      if (!prev.imageData) return prev;

      const activeImageId = prev.imageData.render_id || prev.imageData.id;
      const incomingImageId = imageData?.render_id || imageData?.id;

      return {
        ...prev,
        imageData: activeImageId === incomingImageId
          ? {
              ...prev.imageData,
              display_name: updatedDisplayName,
            }
          : prev.imageData,
      };
    });
  }, [syncAiModalState]);

  const requestAiModalNavigation = useCallback((direction) => {
    const currentState = aiModalStateRef.current;
    const iframeWindow = iframeRef.current?.contentWindow;
    const maxIndex = currentState.navigationImageIds.length - 1;

    if (!iframeWindow || !currentState.isOpen || currentState.isNavigating) return;
    if (direction === 'next' && currentState.currentIndex >= maxIndex) return;
    if (direction === 'previous' && currentState.currentIndex <= 0) return;

    const requestId = aiModalRequestRef.current + 1;
    aiModalRequestRef.current = requestId;

    syncAiModalState({
      ...currentState,
      isNavigating: true,
      pendingRequestId: requestId,
    });

    iframeWindow.postMessage(
      {
        type: 'ALL3D_IMAGE_GALLERY_AI_MODAL_NAVIGATE',
        payload: {
          direction,
          currentIndex: currentState.currentIndex,
          currentImageId: currentState.imageData?.render_id || currentState.imageData?.id,
          navigationImageIds: currentState.navigationImageIds,
          sessionId: currentState.sessionId,
          requestId,
        },
      },
      targetOrigin
    );
  }, [syncAiModalState, targetOrigin]);

  const goToNextAiImage = useCallback(() => {
    requestAiModalNavigation('next');
  }, [requestAiModalNavigation]);

  const goToPreviousAiImage = useCallback(() => {
    requestAiModalNavigation('previous');
  }, [requestAiModalNavigation]);

  useEffect(() => {
    const onMessage = (event) => {
      if (targetOrigin !== '*' && event.origin !== targetOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      const { type, payload } = event?.data || {};

      if (type === 'ALL3D_USER_CONTEXT_REQUEST') {
        postUserContext();
      } else if (type === 'ALL3D_NAVIGATE') {
        window.location.href = event.data.url || '';
      } else if (type === 'ALL3D_REPLACE_URL' && typeof event.data.url === 'string') {
        window.history.replaceState({}, '', event.data.url);
      } else if (type === 'ALL3D_IMAGE_GALLERY_OPEN_STANDARD_MODAL') {
        aiModalSessionRef.current += 1;
        syncAiModalState(createAiModalState({
          sessionId: aiModalSessionRef.current,
        }));
        setCompareSiloModalOpen(false);
        setStandardModalState({
          isOpen: true,
          images: payload?.images || [],
          currentIndex: payload?.currentIndex || 0,
          currentType: payload?.currentType || 'all',
          imagesCount: payload?.imagesCount || {},
        });
      } else if (type === 'ALL3D_IMAGE_GALLERY_OPEN_AI_MODAL') {
        const currentIndex = payload?.currentIndex || 0;
        const nextImageData = buildAiModalImageData(payload?.selectedImageData, payload?.selectedImageData);
        const nextSessionId = aiModalSessionRef.current + 1;
        aiModalSessionRef.current = nextSessionId;

        setStandardModalState((prev) => ({ ...prev, isOpen: false }));
        setCompareSiloModalOpen(false);
        syncAiModalState(createAiModalState({
          isOpen: true,
          imageData: nextImageData,
          currentIndex,
          navigationImageIds: normalizeNavigationImageIds(payload?.navigationImageIds, nextImageData),
          sessionId: nextSessionId,
        }));
      } else if (type === 'ALL3D_IMAGE_GALLERY_AI_MODAL_NAVIGATION_RESULT') {
        const currentState = aiModalStateRef.current;
        if (
          payload?.sessionId !== currentState.sessionId ||
          payload?.requestId !== currentState.pendingRequestId
        ) {
          return;
        }

        syncAiModalState((prev) => ({
          ...prev,
          imageData: payload?.success === false
            ? prev.imageData
            : buildAiModalImageData(payload?.selectedImageData, payload?.selectedImageData),
          currentIndex: payload?.success === false ? prev.currentIndex : (payload?.currentIndex || 0),
          isNavigating: false,
          pendingRequestId: null,
        }));
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [buildAiModalImageData, postUserContext, syncAiModalState, targetOrigin]);

  const hasPreviousAiImage = aiModalState.currentIndex > 0;
  const hasNextAiImage = aiModalState.currentIndex < aiModalState.navigationImageIds.length - 1;

  return (
    <CustomerMainLayout selected="25" remove_global_padding={true}>
      <div style={{ width: '100%', height: '100%' }}>
        <iframe
          ref={iframeRef}
          title="Image Gallery"
          src={iframeSrc}
          onLoad={postUserContext}
          style={{
            width: '100%',
            height: '100%',
            border: '0',
            display: 'block',
          }}
        />
      </div>

      <TwoDImagesModal
        isVisible={standardModalState.isOpen}
        handleOk={closeStandardModal}
        handleCancel={closeStandardModal}
        currentfile={standardModalState.images[standardModalState.currentIndex]}
        filteredImages={standardModalState.images}
        renderDelete={() => null}
        renderDownload={() => null}
        renderGenerateHighResImage={() => null}
        setType={() => {}}
        Type={standardModalState.currentType}
        imagesCount={standardModalState.imagesCount}
        renderCollaborationTooltip={() => null}
        renderReportButton={() => null}
        renderFavouriteButton={() => null}
        updatedRenderNameInImagesList={() => {}}
        markFavorite={() => {}}
        unmarkFavorite={() => {}}
        handleSubmitForReview={() => {}}
      />

      <AIEditorModalViewer
        from="gallery"
        ai_page={true}
        isOpen={aiModalState.isOpen}
        onClose={closeAiModal}
        imageData={aiModalState.imageData}
        compareSiloModalOpen={compareSiloModalOpen}
        setCompareSiloModalOpen={setCompareSiloModalOpen}
        goToNextImage={hasNextAiImage ? goToNextAiImage : undefined}
        goToPreviousImage={hasPreviousAiImage ? goToPreviousAiImage : undefined}
        imageDisplayLoader={aiModalState.isNavigating}
        renderDelete={() => null}
        updatedRenderNameInImagesList={updateAiModalImageName}
        markFavorite={() => {}}
        unmarkFavorite={() => {}}
        promptsLoading={false}
        handleCollaborateAiImage={() => {}}
        handleSubmitForReviewImageLibrary={() => {}}
      />
    </CustomerMainLayout>
  );
};

export default MigratedImageGallery;
